import '../styles/home.css'
import React from 'react'

import LandingGlobalHeader from '../components/landing/LandingGlobalHeader'
import LandingFooter from '../components/landing/LandingFooter'
import LandingIcon from '../components/landing/LandingIcon'
import useLandingAuth from '../components/landing/useLandingAuth'

const ABOUT_STATS = [
  { value: '26', label: 'Countries Covered' },
  { value: '4,500+', label: 'Time Series' },
  { value: '35+', label: 'Years of Data' },
]

const ABOUT_SECTIONS = [
  {
    number: '01',
    tag: 'Foundation',
    title: 'What is Latin Macro Watch?',
    tone: 'light',
    paragraphs: [
      'Latin Macro Watch (LMW) is a comprehensive macroeconomic database developed by the Inter-American Development Bank. It consolidates official statistics from central banks, ministries of finance, and national statistical offices across all 26 borrowing member countries into a single, standardized platform.',
      'Every indicator in LMW is sourced directly from the national authority responsible for that data. Statistics are reported exactly as published by those authorities, ensuring full institutional traceability and auditability.',
    ],
  },
  {
    number: '02',
    tag: 'The Challenge',
    title: 'The Problem We Solve',
    tone: 'alt',
    paragraphs: [
      'Accessing macroeconomic data in Latin America and the Caribbean requires navigating dozens of institutions, formats, and release calendars. Each national authority publishes data through its own portal, using its own conventions for frequency, units, and structure.',
      'This fragmentation makes cross-country analysis time-consuming and error-prone. Researchers and analysts spend significant effort simply collecting and harmonizing data before any meaningful analysis can begin.',
    ],
  },
  {
    number: '03',
    tag: 'Differentiators',
    title: 'What Makes LMW Different',
    tone: 'light',
    paragraphs: [
      'LMW brings all official data into one place, harmonized under consistent definitions and updated on a monthly basis. It provides ready-to-use, comparable time series designed specifically for regional analysis.',
      'Unlike global databases that update annually or quarterly, LMW is refreshed every month, incorporating the latest official releases as they become available. More than a third of LMW series are unavailable in other global platforms, including fiscal sub-sector breakdowns, financial macro indicators, and GDP components by tradeable classification.',
    ],
  },
  {
    number: '04',
    tag: 'Scope',
    title: 'Coverage',
    tone: 'alt',
    paragraphs: [
      'The platform covers six core thematic areas, providing annual, quarterly, and monthly series across all 26 borrowing countries. Each base indicator is systematically expanded into multiple analytical transformations, delivering over 4,500 ready-to-use series.',
    ],
    list: [
      'Economic Activity and Employment',
      'Prices and Wages',
      'Fiscal Accounts and Public Debt',
      'External Accounts',
      'Money and Banking',
      'Financial Markets',
    ],
  },
  {
    number: '05',
    tag: 'Users',
    title: 'Who It Is For',
    tone: 'light',
    paragraphs: [
      'LMW is designed for everyone who requires reliable, comparable, and up-to-date macroeconomic data on Latin America and the Caribbean.',
    ],
    cards: [
      {
        title: 'Policymakers',
        description: 'Track macroeconomic trends and support evidence-based decisions.',
        icon: 'landmark',
      },
      {
        title: 'Researchers',
        description: 'Access harmonized, cross-country time series ready for econometric analysis.',
        icon: 'flask',
      },
      {
        title: 'International Orgs',
        description: 'Benchmark regional performance with consistent, official data.',
        icon: 'globe',
      },
      {
        title: 'Analysts',
        description: 'Retrieve standardized indicators for any LAC country in seconds.',
        icon: 'searchChart',
      },
    ],
  },
  {
    number: '06',
    tag: 'Impact',
    title: 'Why It Matters',
    tone: 'alt',
    paragraphs: [
      'By reducing the time required to access and standardize data, LMW allows users to focus on analysis rather than data collection, enabling faster and more informed decision-making.',
      'Retrieving a base indicator takes around 30 seconds in LMW compared to over 100 seconds using traditional sources, more than three times faster. Multiplied across hundreds of indicators and dozens of countries, this represents a meaningful reduction in the cost of evidence-based analysis.',
    ],
    cta: {
      href: 'https://data.iadb.org/dataset/latin-macro-watch-dataset',
      label: 'Access the Dataset',
    },
  },
]

const PLATFORM_SECTIONS = [
  {
    title: 'Home',
    description: 'The Home page provides a general overview of the platform, including key statistics, thematic areas of coverage, and featured analytical content. It serves as the entry point for first-time users and as a quick reference for returning ones.',
  },
  {
    title: 'About LMW',
    description: 'This section explains the scope, purpose, and institutional context of the database. It covers the countries and thematic areas included, the motivation behind the platform, and the standards applied to data compilation and harmonization.',
  },
  {
    title: 'Dashboard',
    description: 'The Dashboard is the platform\'s interactive visualization tool. Users can select any indicator, country, and time range to generate charts and explore macroeconomic trends across the region.',
  },
  {
    title: 'Data Center',
    description: 'The Data Center is the primary download interface. Users can customize requests by selecting indicators, countries, frequency, and date ranges.',
  },
  {
    title: 'Methodology',
    description: 'The Methodology section provides the full technical documentation for the database, including indicator definitions, primary sources, and construction notes.',
  },
]

function AboutLMWScreen() {
  const { loadingUser, userInfo, openModal, logoutHandler, authModals } = useLandingAuth()

  return (
    <div className='landing-page-shell landing-secondary-page'>
      <LandingGlobalHeader
        loadingUser={loadingUser}
        userInfo={userInfo}
        onOpenModal={openModal}
        onLogout={logoutHandler}
      />

      <main>
        <section className='landing-page-hero landing-page-hero-about'>
          <div className='container landing-page-hero-inner'>
            <span className='landing-page-eyebrow'>Institutional Overview</span>
            <h1>About Latin Macro Watch</h1>
            <div className='landing-page-hero-bar'></div>
          </div>
        </section>

        <section className='landing-stat-band'>
          <div className='container landing-stat-band-inner'>
            {ABOUT_STATS.map(item => (
              <div className='landing-stat-band-item' key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        {ABOUT_SECTIONS.map((section, index) => (
          <section className={`landing-about-section ${section.tone === 'alt' ? 'is-alt' : ''}`} key={section.number}>
            <div className={`container landing-about-layout ${index % 2 === 1 ? 'is-reverse' : ''}`}>
              <div className='landing-about-label-col'>
                <div className='landing-about-number'>{section.number}</div>
                <p className='landing-about-tag'>{section.tag}</p>
                <h2>{section.title}</h2>
              </div>
              <div className='landing-about-content-col'>
                {section.paragraphs.map(paragraph => (
                  <p key={paragraph}>{paragraph}</p>
                ))}

                {section.list ? (
                  <ul className='landing-about-list'>
                    {section.list.map(item => <li key={item}>{item}</li>)}
                  </ul>
                ) : null}

                {section.cards ? (
                  <div className='landing-about-card-grid'>
                    {section.cards.map(card => (
                      <article className='landing-about-mini-card' key={card.title}>
                        <div className='landing-about-mini-icon'><LandingIcon name={card.icon} /></div>
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                      </article>
                    ))}
                  </div>
                ) : null}

                {section.cta ? (
                  <div className='landing-about-cta-row'>
                    <a className='landing-hero-primary compact' href={section.cta.href} target='_blank' rel='noreferrer'>
                      <LandingIcon name='database' size={18} />
                      {section.cta.label}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        ))}

        <section className='landing-about-section is-guide'>
          <div className='container landing-about-layout'>
            <div className='landing-about-label-col'>
              <p className='landing-about-tag'>Platform</p>
              <h2>How to Use Latin Macro Watch</h2>
            </div>
            <div className='landing-about-content-col'>
              <p>
                The platform is organized into six sections, each designed for a specific use case. The landing, search experience, and future editorial layers work together as one product surface.
              </p>
              <div className='landing-about-flow-list'>
                {PLATFORM_SECTIONS.map(item => (
                  <article className='landing-about-flow-item' key={item.title}>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
      {authModals}
    </div>
  )
}

export default AboutLMWScreen
